import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession, createPortalSession } from '../lib/firestore';
import { Button } from '../components/ui/Button';

// Add to .env.local:
// REACT_APP_STRIPE_PRICE_ID=price_xxxxxxxxxxxxxxxxxx
const PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID ?? '';

function TrialCountdown({ trialEndsAt }: { trialEndsAt: any }) {
  if (!trialEndsAt) return null;
  const end = trialEndsAt.toDate ? trialEndsAt.toDate() : new Date(trialEndsAt);
  const daysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86_400_000));
  const expired = daysLeft === 0;

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '8px 16px',
      background: expired ? 'rgba(220,38,38,0.12)' : 'rgba(201,168,76,0.1)',
      border: `1px solid ${expired ? 'rgba(220,38,38,0.3)' : 'rgba(201,168,76,0.25)'}`,
      borderRadius: 6,
      fontFamily: 'var(--font-mono)',
      fontSize: '0.7rem',
      letterSpacing: '0.08em',
      color: expired ? '#f87171' : 'var(--gold)',
    }}>
      {expired
        ? '⚠ Trial expired'
        : `◌ ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left on free trial`}
    </div>
  );
}

function CheckItem({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
      <span style={{ color: 'var(--gold)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem', marginTop: 1 }}>✓</span>
      <span style={{ fontSize: '0.9rem', color: 'var(--chalk-dim)', lineHeight: 1.4 }}>{children}</span>
    </div>
  );
}

export function SubscribePage() {
  const { user, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isActive     = profile?.subscriptionStatus === 'active';
  const isCancelled  = profile?.subscriptionStatus === 'cancelled';
  const justSuccess  = searchParams.get('success') === 'true';
  const wasCancelled = searchParams.get('cancelled') === 'true';

  const handleSubscribe = async () => {
    if (!user) return;
    if (!PRICE_ID) {
      setError('Price ID not configured. Add REACT_APP_STRIPE_PRICE_ID to .env.local.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = await createCheckoutSession(user.uid, PRICE_ID);
      window.location.href = url;
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleManage = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const url = await createPortalSession(user.uid);
      window.location.href = url;
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', animation: 'fadeUp 0.4s ease' }}>

      {/* Header */}
      <div style={{ marginBottom: 36 }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.6rem', letterSpacing: '0.2em',
          textTransform: 'uppercase', color: 'var(--gold-dim)', marginBottom: 8,
        }}>
          Subscription
        </div>
        <h1 style={{ fontSize: '2rem', marginBottom: 8 }}>147coach Pro</h1>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.95rem', lineHeight: 1.6 }}>
          Full access to all features — session logging, drill tracking, progress charts, and tier analytics.
        </p>
      </div>

      {/* Banners */}
      {justSuccess && (
        <div style={{
          padding: '14px 20px', marginBottom: 24,
          background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 8, color: '#6ee7b7',
          fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.05em',
        }}>
          ✓ Subscription activated — welcome to Pro!
        </div>
      )}
      {wasCancelled && (
        <div style={{
          padding: '14px 20px', marginBottom: 24,
          background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 8, color: 'var(--gold-dim)',
          fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.05em',
        }}>
          ◌ Checkout cancelled — no charge was made.
        </div>
      )}

      {/* Trial badge */}
      {!isActive && profile?.trialEndsAt && (
        <div style={{ marginBottom: 28 }}>
          <TrialCountdown trialEndsAt={profile.trialEndsAt} />
        </div>
      )}

      {/* Pricing card */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(201,168,76,0.2)',
        borderRadius: 12,
        padding: '32px',
        marginBottom: 24,
      }}>
        {/* Price */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 6 }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 900,
            color: 'var(--chalk)', lineHeight: 1,
          }}>
            €6.50
          </span>
          <span style={{
            color: 'var(--chalk-faint)', fontFamily: 'var(--font-mono)',
            fontSize: '0.7rem', letterSpacing: '0.1em', marginBottom: 6,
          }}>
            /month
          </span>
        </div>
        <p style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--chalk-faint)',
          letterSpacing: '0.05em', marginBottom: 28,
        }}>
          Billed in your local currency at checkout
        </p>

        {/* Features */}
        <div style={{ marginBottom: 28 }}>
          <CheckItem>Unlimited session &amp; match logging</CheckItem>
          <CheckItem>Full drill library with performance tracking</CheckItem>
          <CheckItem>Progress charts — pot rate, break avg, safety over time</CheckItem>
          <CheckItem>Tier progression system (Beginner → Master)</CheckItem>
          <CheckItem>AI-generated insights after every session</CheckItem>
          <CheckItem>Priority support</CheckItem>
        </div>

        {/* CTA */}
        {isActive ? (
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
              fontFamily: 'var(--font-mono)', fontSize: '0.7rem',
              color: '#6ee7b7', letterSpacing: '0.05em',
            }}>
              ✓ You're on Pro
            </div>
            <Button variant="ghost" onClick={handleManage} loading={loading}>
              Manage Billing →
            </Button>
          </div>
        ) : isCancelled ? (
          <div>
            <p style={{ fontSize: '0.8rem', color: 'var(--chalk-faint)', marginBottom: 16 }}>
              Your subscription was cancelled. Resubscribe to regain access.
            </p>
            <Button fullWidth onClick={handleSubscribe} loading={loading}>
              Resubscribe
            </Button>
          </div>
        ) : (
          <Button fullWidth onClick={handleSubscribe} loading={loading}>
            Subscribe — €6.50/mo
          </Button>
        )}

        {!isActive && (
          <p style={{
            marginTop: 12, textAlign: 'center',
            fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
            color: 'var(--chalk-faint)', letterSpacing: '0.05em',
          }}>
            Secure checkout via Stripe · Cancel anytime
          </p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)',
          borderRadius: 6, color: '#f87171',
          fontFamily: 'var(--font-mono)', fontSize: '0.7rem',
        }}>
          {error}
        </div>
      )}
    </div>
  );
}